import React, { Component, createRef } from "react";
import { ImageBackground, Text, TouchableOpacity, View } from "react-native";
import Icon from "react-native-vector-icons/Feather";
import Modal from "modal-react-native-web";

import { FILE_TYPE, MAX_FILE_SIZE } from "consts";
import { Video } from "components";

import CameraController from "./controller";
import { styles } from "./styles";

export class Camera extends Component {
  camera = createRef();

  constructor(props) {
    super(props);

    const { photo, video } = props;

    const allowedMedia =
      (photo && video && AM.BOTH) || (video && AM.VIDEO) || AM.PHOTO;

    const selectedMedia = allowedMedia === AM.VIDEO ? AM.VIDEO : AM.PHOTO;

    this.state = {
      image: null,
      video: null,
      allowedMedia,
      selectedMedia,
      visible: false,
      recordingState: RS.IDLE,
      authorized: false,
    };
  }

  closeCamera = () => {
    this.shootAgain();
    this.setState({ visible: false });
  };

  shootAgain = () => {
    this.setState({ recordingState: RS.IDLE, image: null, video: null });
  };

  switchMode = () => {
    if (this.state.selectedMedia === AM.PHOTO) {
      this.setState({ selectedMedia: AM.VIDEO });
    } else {
      this.setState({ selectedMedia: AM.PHOTO });
    }
  };

  componentWillUnmount = () => {
    this.closeCamera();
  };

  switchDirection = () => {
    this.camera.current?.switchDirection();
  };

  onPermissionGranted = () => {
    this.setState({ authorized: true });
  };

  // ====================== PHOTO ====================== //

  takePicture = async () => {
    const image = await this.camera.current.takePictureAsync();
    this.setState({ recordingState: RS.PREVIEW, image });
  };

  confirmPicture = async () => {
    const valid = await this.isValidSize(this.state.image);
    const uri = valid ? this.state.image : "";
    this.props.onCapture(uri, FILE_TYPE.IMAGE);
    this.closeCamera();
  };

  isValidSize = async uri => {
    const response = await fetch(uri);
    const blob = await response.blob();
    return blob.size <= MAX_FILE_SIZE;
  };

  // ====================== VIDEO ====================== //

  startRecording = () => {
    this.setState({ recordingState: RS.RECORDING });
    this.camera.current.recordAsync();
  };

  stopRecording = () => {
    this.camera.current.stopRecording();
  };

  getRecordedVideo = video => {
    this.setState({ recordingState: RS.PREVIEW, video });
  };

  confirmRecording = async () => {
    const valid = await this.isValidSize(this.state.video);
    const uri = valid ? this.state.video : "";
    this.props.onCapture(uri, FILE_TYPE.VIDEO);
    this.closeCamera();
  };

  // ====================== RENDERS ====================== //

  render = () => {
    return (
      <>
        <TouchableOpacity onPress={() => this.setState({ visible: true })}>
          {this.props.children}
        </TouchableOpacity>
        <Modal
          visible={this.state.visible}
          animationType="slide"
          transparent={true}
        >
          <View style={styles.modal}>
            <View style={styles.modalContainer}>
              {this.state.visible && (
                <>
                  {this.state.recordingState === RS.PREVIEW
                    ? this.renderPreview()
                    : this.renderCamera()}
                </>
              )}
            </View>
          </View>
        </Modal>
      </>
    );
  };

  renderCamera = () => {
    return (
      <View style={{ flex: 1 }}>
        <CameraController
          style={{ flex: 1 }}
          ref={this.camera}
          getRecordedVideo={this.getRecordedVideo}
          onPermissionGranted={this.onPermissionGranted}
        >
          <View style={styles.cameraContainer}>{this.renderControls()}</View>
        </CameraController>
      </View>
    );
  };

  // ====================== CONTROLS ====================== //

  renderControls = () => {
    return (
      <View style={styles.controls}>
        <View style={styles.controlGroups}>{this.renderCloseButton()}</View>
        <View style={styles.controlGroups}>{this.renderCameraControls()}</View>
        <View style={styles.controlGroups}>{this.renderModeSwitch()}</View>
      </View>
    );
  };

  renderCloseButton = () => {
    if (this.state.recordingState === RS.IDLE) {
      return (
        <View style={{ marginLeft: 20 }}>
          <CameraControl
            label="exit"
            icon="arrow-left"
            onPress={this.closeCamera}
          />
        </View>
      );
    }
  };

  renderModeSwitch = () => {
    const { allowedMedia, selectedMedia, recordingState } = this.state;
    if (allowedMedia !== AM.BOTH || recordingState !== RS.IDLE) {
      return;
    }

    const takingPhoto = selectedMedia === AM.PHOTO;
    const switchTo = takingPhoto ? "video" : "photo";
    const icon = takingPhoto ? "video" : "camera";

    return (
      <CameraControl label={switchTo} icon={icon} onPress={this.switchMode} />
    );
  };

  renderCameraControls = () => {
    const takingPhoto = this.state.selectedMedia === AM.PHOTO;

    const shoot = this.state.authorized
      ? takingPhoto
        ? this.takePicture
        : this.startRecording
      : null;
    const shootLabel = takingPhoto ? "snap" : "record";
    const confirm = takingPhoto ? this.confirmPicture : this.confirmRecording;

    switch (this.state.recordingState) {
      case RS.IDLE:
        return (
          <CameraControl
            icon="circle"
            size={50}
            label={shootLabel}
            onPress={shoot}
          />
        );
      case RS.PREVIEW:
        return (
          <>
            <CameraControl
              icon="rotate-ccw"
              label="again"
              onPress={this.shootAgain}
            />
            <CameraControl icon="check" label="confirm" onPress={confirm} />
          </>
        );
      case RS.RECORDING:
        return (
          <CameraControl
            label="stop"
            color="red"
            size={50}
            icon="stop-circle"
            onPress={this.stopRecording}
          />
        );
    }
  };

  // ====================== PREVIEW ====================== //

  renderPreview = () => {
    const takingPhoto = this.state.selectedMedia === AM.PHOTO;

    const Previewer = takingPhoto ? ImageBackground : Video;
    const uri = takingPhoto ? this.state.image : this.state.video;

    if (!uri) {
      return;
    }

    return (
      <>
        <View style={{ alignSelf: "center" }}>
          <Previewer
            repeat
            resizeMode="contain"
            style={styles.imagePreview}
            source={{ uri }}
          />
        </View>
        {this.renderControls()}
      </>
    );
  };
}

const CameraControl = props => {
  const { icon, size = 30, color = "white", label, onPress } = props;
  return (
    <TouchableOpacity onPress={onPress} style={[styles.cameraControl]}>
      <Icon name={icon} size={size} color={color} />
      <Text style={styles.cameraControlText}>{label}</Text>
    </TouchableOpacity>
  );
};

const RS = {
  IDLE: "I",
  PREVIEW: "P",
  RECORDING: "R",
};

const AM = {
  PHOTO: "P",
  VIDEO: "V",
  BOTH: "B",
};
